import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { track } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreRegisterForm = () => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [count, setCount] = useState(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pre-register/count`);
      if (typeof data?.count === 'number') setCount(data.count);
    } catch {
      /* silent — counter is non-critical */
    }
  }, []);

  useEffect(() => {
    loadCount();
  }, [loadCount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!firstName.trim() || !email.trim()) {
      toast.error('Please enter your name and email to register.');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/pre-register`, {
        firstName: firstName.trim(),
        email: email.trim(),
        reason: reason.trim() || null,
        consent: true,
      });
      if (data?.success) {
        setSuccess(true);
        loadCount();
        track('register_success', { value: 'main' });
        toast.success(`Thank you ${firstName.trim().split(' ')[0]}. Check your inbox — something is coming.`);
      } else {
        toast.error(data?.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      id="register"
      data-testid="register-section"
      style={{
        background: 'var(--yup-ink)',
        padding: '100px 24px',
      }}
    >
      <div style={{ maxWidth: '560px', margin: '0 auto', textAlign: 'center' }}>
        <div
          className="yup-eyebrow"
          style={{ color: 'var(--yup-warm)', marginBottom: '20px' }}
        >
          Register your interest
        </div>
        <h2
          className="font-serif"
          style={{
            fontSize: 'clamp(30px, 5vw, 52px)',
            fontWeight: 300,
            color: 'var(--yup-bg)',
            lineHeight: 1.15,
            marginBottom: '20px',
            letterSpacing: '-0.5px',
          }}
        >
          Ready to hear<br />
          what your voice<br />
          <em style={{ fontStyle: 'italic', color: 'rgba(249, 245, 239, 0.5)' }}>
            actually sounds like?
          </em>
        </h2>
        <p
          style={{
            color: 'rgba(249, 245, 239, 0.5)',
            fontSize: '15px',
            marginBottom: count !== null && count > 0 ? '16px' : '40px',
            lineHeight: 1.7,
          }}
        >
          Add your name when you're ready. The rest can move at your pace.
        </p>

        {count !== null && count > 0 && (
          <p
            data-testid="register-count"
            className="flex items-center justify-center"
            style={{
              gap: '12px',
              fontSize: '11px',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: 'var(--yup-warm)',
              marginBottom: '40px',
              lineHeight: 1,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'block',
                width: '32px',
                height: '1px',
                background: 'var(--yup-warm)',
                opacity: 0.5,
              }}
            />
            {count.toLocaleString()} {count === 1 ? 'voice' : 'voices'} so far
            <span
              aria-hidden="true"
              style={{
                display: 'block',
                width: '32px',
                height: '1px',
                background: 'var(--yup-warm)',
                opacity: 0.5,
              }}
            />
          </p>
        )}

        {success ? (
          <div
            data-testid="register-success"
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderLeft: '2px solid var(--yup-warm)',
              padding: '40px 32px',
              textAlign: 'left',
            }}
          >
            <h3
              className="font-serif"
              style={{
                fontSize: '28px',
                fontWeight: 300,
                color: 'var(--yup-bg)',
                marginBottom: '14px',
                letterSpacing: '-0.5px',
              }}
            >
              Thank you. You're with us.
            </h3>
            <p
              style={{
                color: 'rgba(249, 245, 239, 0.6)',
                fontSize: '15px',
                lineHeight: 1.7,
              }}
            >
              Five gentle messages will arrive over the next five days — one each morning. After that, the 30 days are yours. Self-guided. Soft. As slow as you need.
              <br /><br />
              When something lands, share it with <strong style={{ color: 'var(--yup-bg)', fontWeight: 500 }}>#unmaskyourvoice</strong>. Every voice that shares helps the next person find theirs.
            </p>
          </div>
        ) : (
          <form data-testid="register-form" onSubmit={handleSubmit}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              <input
                type="text"
                name="firstName"
                placeholder="Your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                data-testid="register-firstname-input"
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'var(--yup-bg)',
                  padding: '16px 20px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px',
                  fontWeight: 300,
                  width: '100%',
                  outline: 'none',
                  borderRadius: 0,
                  WebkitAppearance: 'none',
                  transition: 'border-color 0.2s',
                }}
              />
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'var(--yup-bg)',
                  padding: '16px 20px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px',
                  fontWeight: 300,
                  width: '100%',
                  outline: 'none',
                  borderRadius: 0,
                  WebkitAppearance: 'none',
                  transition: 'border-color 0.2s',
                }}
              />
              <textarea
                name="reason"
                placeholder="What made you stop and read this today?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="register-reason-input"
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'var(--yup-bg)',
                  padding: '16px 20px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px',
                  fontWeight: 300,
                  width: '100%',
                  outline: 'none',
                  borderRadius: 0,
                  WebkitAppearance: 'none',
                  resize: 'none',
                  height: '100px',
                  transition: 'border-color 0.2s',
                }}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="register-submit-button"
              style={{
                width: '100%',
                background: 'var(--yup-warm)',
                color: 'var(--yup-bg)',
                border: 'none',
                padding: '18px',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '15px',
                fontWeight: 500,
                letterSpacing: '0.5px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.2s, transform 0.2s',
                marginTop: '4px',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Sending…' : "Yes — I'd like to begin"}
            </button>

            <p
              style={{
                fontSize: '12px',
                color: 'rgba(249, 245, 239, 0.25)',
                marginTop: '16px',
                lineHeight: 1.6,
              }}
            >
              Your email stays between us. Step away whenever you need to.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default PreRegisterForm;
